import React from "react";
import { useQueryClient } from "react-query";
import {
  CameraCreateDto,
  CameraEditDto,
  PaginatedListOfCameraItemDto,
  useCameraApi,
} from "~/api/GeneratedApi";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

export const camerasQueryKey = "cameras" as const;

type EditArgs = {
  id: number;
  dto: CameraEditDto;
};

type UseCamerasArgs = {
  onMutationSuccess?: () => void;
};
export function useCameras({ onMutationSuccess }: UseCamerasArgs = {}) {
  const { getCameras, addCamera, deleteCamera, editCamera } = useCameraApi();
  const qc = useQueryClient();

  const handleMutationUpdate = React.useCallback(() => {
    onMutationSuccess?.();
    void qc.invalidateQueries(camerasQueryKey);
  }, [onMutationSuccess, qc]);

  const listQuery = useCustomQuery(camerasQueryKey, () => getCameras(), {
    staleTime: Number.POSITIVE_INFINITY,
    select: React.useCallback(
      (d: PaginatedListOfCameraItemDto) => d.results,
      [],
    ),
  });

  const getCameraById = React.useCallback(
    (id: number) => {
      return listQuery.data?.find((x) => x.id === id) ?? null;
    },
    [listQuery.data],
  );

  const removeMutation = useCustomMutation((id: number) => deleteCamera(id), {
    onSuccess: handleMutationUpdate,
  });
  const editMutation = useCustomMutation(
    ({ dto, id }: EditArgs) => editCamera(dto, id),
    { onSuccess: handleMutationUpdate },
  );
  const addMutation = useCustomMutation(
    (dto: CameraCreateDto) => addCamera(dto),
    { onSuccess: handleMutationUpdate },
  );

  return {
    listQuery,
    getCameraById,
    removeMutation,
    editMutation,
    addMutation,
    getCameras,
  };
}
